import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { Trophy, Sparkles, Activity, Users, MessageSquare, Zap, RefreshCw } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtChars = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "k";
  return String(v);
};
const shortDate = (iso) => {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

const TABS = [
  { id: "leaderboard", label: "Leaderboard", icon: Trophy },
  { id: "charts", label: "Charts", icon: Activity },
  { id: "activity", label: "Activity log", icon: MessageSquare },
];

const RANGES = [
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
];

export default function AdminLogs() {
  const [tab, setTab] = useState("leaderboard");
  const [range, setRange] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const refresh = React.useCallback(() => {
    setLoading(true);
    return Promise.all([
      api.get("/admin/analytics", { params: { days: range, _: Date.now() } }),
      api.get("/admin/logs", { params: { limit: 300, _: Date.now() } }),
    ])
      .then(([a, l]) => {
        setAnalytics(a.data);
        setLogs(l.data);
        setRefreshedAt(new Date());
      })
      .finally(() => setLoading(false));
  }, [range]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh when the admin returns to this tab/window
  useEffect(() => {
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  const totals = analytics?.totals || {};
  const topUsers = analytics?.top_users || [];
  const topTools = analytics?.top_tools || [];
  const usageSeries = useMemo(
    () =>
      (analytics?.usage_timeseries || []).map((d) => ({
        ...d,
        label: shortDate(d.date),
      })),
    [analytics]
  );
  const newUsersSeries = useMemo(
    () =>
      (analytics?.new_users_timeseries || []).map((d) => ({
        ...d,
        label: shortDate(d.date),
      })),
    [analytics]
  );

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Messages" value={fmt(totals.messages)} accent="emerald" icon={MessageSquare} />
        <Kpi label="Output chars" value={fmtChars(totals.output_chars)} accent="emerald" icon={Zap} />
        <Kpi label="Active users" value={fmt(totals.active_users)} accent="gold" icon={Users} />
        <Kpi label="New signups" value={fmt(totals.new_users)} accent="gold" icon={Sparkles} />
      </div>

      {/* Tab + range bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 p-1 rounded-xl border border-zinc-800 bg-zinc-950/60">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`logs-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.18em] transition-colors ${
                  tab === t.id
                    ? "bg-emerald-500/10 text-emerald-300"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="logs-refresh-btn"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.18em] border border-zinc-800 bg-zinc-950/60 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors disabled:opacity-50"
            title={refreshedAt ? `Last refreshed: ${refreshedAt.toLocaleTimeString()}` : "Refresh analytics"}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <div className="flex items-center gap-1 p-1 rounded-xl border border-zinc-800 bg-zinc-950/60">
            {RANGES.map((r) => (
              <button
                key={r.id}
                data-testid={`logs-range-${r.id}`}
                onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.18em] transition-colors ${
                  range === r.id
                    ? "bg-emerald-500/10 text-emerald-300"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-zinc-500 text-sm">Loading analytics...</div>
      )}

      {!loading && tab === "leaderboard" && (
        <Leaderboard topUsers={topUsers} topTools={topTools} />
      )}
      {!loading && tab === "charts" && (
        <Charts usageSeries={usageSeries} newUsersSeries={newUsersSeries} topTools={topTools} />
      )}
      {!loading && tab === "activity" && <ActivityLog logs={logs} />}
    </div>
  );
}

const Kpi = ({ label, value, accent, icon: Icon }) => (
  <div className="rounded-2xl border border-zinc-800/80 bg-zinc-950/40 p-5 grain relative overflow-hidden">
    <div
      className={`absolute top-0 left-0 right-0 h-px ${
        accent === "gold"
          ? "bg-gradient-to-r from-transparent via-amber-400/50 to-transparent"
          : "bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent"
      }`}
    />
    <div className="flex items-center gap-2">
      {Icon && (
        <Icon
          className={`w-3.5 h-3.5 ${
            accent === "gold" ? "text-amber-300" : "text-emerald-400"
          }`}
        />
      )}
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        {label}
      </div>
    </div>
    <div
      className={`font-display font-black tracking-tighter mt-2 text-3xl ${
        accent === "gold" ? "text-amber-300" : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

const Leaderboard = ({ topUsers, topTools }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    <div
      data-testid="leaderboard-users"
      className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        <Trophy className="w-3.5 h-3.5 text-amber-300" /> Most active members
      </div>
      <div className="divide-y divide-zinc-900">
        {topUsers.length === 0 && (
          <div className="px-5 py-12 text-center text-zinc-500 text-sm">
            No member activity in this window.
          </div>
        )}
        {topUsers.slice(0, 10).map((u, i) => (
          <div
            key={u.user_id}
            data-testid={`top-user-${u.user_id}`}
            className="flex items-center gap-3 px-5 py-3"
          >
            <Rank n={i + 1} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">
                {u.user_name || u.user_email || "—"}
              </div>
              <div className="text-[11px] text-zinc-500 truncate">
                {u.user_email}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-mono text-emerald-300">
                {fmt(u.messages)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                msgs · {u.tools_used || 0} agents
              </div>
            </div>
            <div className="text-right hidden sm:block w-16">
              <div className="text-sm font-mono text-amber-300">
                {fmtChars(u.output_chars)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                output
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div
      data-testid="leaderboard-tools"
      className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> Most used agents
      </div>
      <div className="divide-y divide-zinc-900">
        {topTools.length === 0 && (
          <div className="px-5 py-12 text-center text-zinc-500 text-sm">
            No agent activity in this window.
          </div>
        )}
        {topTools.slice(0, 10).map((t, i) => (
          <div
            key={t.tool_id}
            data-testid={`top-tool-${t.tool_id}`}
            className="flex items-center gap-3 px-5 py-3"
          >
            <Rank n={i + 1} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{t.tool_name}</div>
              <div className="text-[11px] text-zinc-500">
                {t.unique_users} unique user{t.unique_users === 1 ? "" : "s"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-mono text-emerald-300">
                {fmt(t.messages)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                msgs
              </div>
            </div>
            <div className="text-right hidden sm:block w-16">
              <div className="text-sm font-mono text-amber-300">
                {fmtChars(t.output_chars)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                output
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Rank = ({ n }) => {
  const colors = ["text-amber-300 border-amber-500/40 bg-amber-500/10",
    "text-zinc-300 border-zinc-500/40 bg-zinc-500/10",
    "text-orange-300 border-orange-500/40 bg-orange-500/10"];
  const cls = n <= 3 ? colors[n - 1] : "text-zinc-500 border-zinc-800";
  return (
    <div className={`w-7 h-7 rounded-md border flex items-center justify-center text-[11px] font-mono font-bold ${cls}`}>
      {n}
    </div>
  );
};

const Charts = ({ usageSeries, newUsersSeries, topTools }) => {
  const toolBars = topTools.slice(0, 8).map((t) => ({
    name: t.tool_name,
    messages: t.messages,
    users: t.unique_users,
  }));

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <ChartCard
        title="Daily messages"
        subtitle="Total chat turns processed per day"
        testid="chart-messages"
      >
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={usageSeries}>
            <defs>
              <linearGradient id="gMsg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
            <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
            <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#27272a55" }} />
            <Area
              type="monotone"
              dataKey="messages"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#gMsg)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Credit usage (output chars)"
        subtitle="Approximate spend on AI generations"
        testid="chart-credits"
      >
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={usageSeries}>
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
            <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
            <YAxis stroke="#71717a" fontSize={11} tickFormatter={fmtChars} />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v) => fmt(v)}
              cursor={{ stroke: "#3f3f46" }}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
            <Line
              type="monotone"
              dataKey="input_chars"
              name="In"
              stroke="#a1a1aa"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="output_chars"
              name="Out"
              stroke="#d4af37"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="New signups"
        subtitle="Members joined per day"
        testid="chart-signups"
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={newUsersSeries}>
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
            <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
            <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#27272a55" }} />
            <Bar dataKey="count" fill="#d4af37" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Top agents by usage"
        subtitle="Messages routed per agent"
        testid="chart-top-tools"
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={toolBars} layout="vertical">
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
            <XAxis type="number" stroke="#71717a" fontSize={11} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              stroke="#71717a"
              fontSize={11}
              width={110}
            />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#27272a55" }} />
            <Bar dataKey="messages" fill="#10b981" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
};

const tooltipStyle = {
  backgroundColor: "#0a0a0a",
  border: "1px solid #27272a",
  borderRadius: 8,
  fontSize: 12,
  color: "#e4e4e7",
};

const ChartCard = ({ title, subtitle, children, testid }) => (
  <div
    data-testid={testid}
    className="border border-zinc-900 rounded-2xl bg-zinc-950/40 p-5"
  >
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
      {title}
    </div>
    {subtitle && (
      <div className="text-xs text-zinc-600 mt-0.5 mb-3">{subtitle}</div>
    )}
    {children}
  </div>
);

const ActivityLog = ({ logs }) => (
  <div className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden">
    <div className="px-4 py-3 border-b border-zinc-900 text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center justify-between">
      <span>{logs.length} recent events</span>
      <span className="font-mono">live · last 300</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <tr>
            <th className="text-left px-4 py-3">When</th>
            <th className="text-left px-4 py-3">User</th>
            <th className="text-left px-4 py-3">Agent</th>
            <th className="text-left px-4 py-3 hidden md:table-cell">Model</th>
            <th className="text-right px-4 py-3 hidden md:table-cell">In</th>
            <th className="text-right px-4 py-3 hidden md:table-cell">Out</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr
              key={l.id}
              data-testid={`log-row-${l.id}`}
              className="border-t border-zinc-900 hover:bg-zinc-900/30"
            >
              <td className="px-4 py-3 font-mono text-xs text-zinc-400">
                {new Date(l.timestamp).toLocaleString()}
              </td>
              <td className="px-4 py-3 text-zinc-300 text-xs">{l.user_email}</td>
              <td className="px-4 py-3 text-white text-sm">{l.tool_name}</td>
              <td className="px-4 py-3 hidden md:table-cell">
                <span className="font-mono text-xs text-zinc-500">{l.model}</span>
              </td>
              <td className="px-4 py-3 text-right hidden md:table-cell font-mono text-xs text-zinc-500">
                {l.input_chars}
              </td>
              <td className="px-4 py-3 text-right hidden md:table-cell font-mono text-xs text-emerald-400">
                {l.output_chars}
              </td>
            </tr>
          ))}
          {logs.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                No usage yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);
